import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Col, Container, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { Breadcrumbs, P } from "../../AbstractElements";
import FileUpload7Cloud from "../Upload/FileUpload7Cloud";
import { getApiBase } from "../../helpers/apiBase";

/**
 * Конфиг загрузки изображения локации (FileUpload7 на облаке).
 * Временно захардкожен; далее поля формы будут пробрасываться в виджет пропсами.
 * Правила crop соответствуют avatar/models/forms/Company (image + image32), индексы уникальны.
 */
const DEFAULT_UPLOAD_CONFIG = {
  maxSize: 5000,
  allowedExtensions: ["jpg", "jpeg", "png", "gif"],
  accept: "image/*",
  controller: "upload",
  cors: true,
  withCredentials: false,
  data: {
    update: JSON.stringify([]),
  },
};

const normalizeFieldErrors = (payload) => {
  if (!payload || typeof payload !== "object") return {};

  const direct = payload.errors;
  if (direct && typeof direct === "object" && !Array.isArray(direct)) {
    // Иногда API возвращает { errors: { errors: {...}, fields: {...} } }.
    if (direct.errors && typeof direct.errors === "object" && !Array.isArray(direct.errors)) {
      return direct.errors;
    }
    return direct;
  }

  const nestedData = payload.data;
  if (nestedData && typeof nestedData === "object" && !Array.isArray(nestedData)) {
    return nestedData;
  }

  return {};
};

const getFirstError = (fieldErrors) => {
  if (!fieldErrors) return "";
  if (Array.isArray(fieldErrors)) return fieldErrors[0] || "";
  if (typeof fieldErrors === "string") return fieldErrors;
  return "";
};

const LocationAdd = () => {
  const navigate = useNavigate();
  const listPath = `${process.env.PUBLIC_URL}/location-list`;
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadedUrls, setUploadedUrls] = useState({});

  useEffect(() => {
    const load = async () => {
      const base = getApiBase();
      if (!base) {
        setError("Не задан REACT_APP_FW_API для запросов к API.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const headers = { "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(`${base}/v1/cpai/location-add-form`, {
          method: "POST",
          headers,
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(data && data.message ? data.message : `Ошибка загрузки формы (код ${res.status}).`);
          return;
        }

        setSchema(data);
        setValues(data && data.values ? data.values : {});
      } catch (e) {
        setError("Ошибка сети при загрузке формы.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleUploadSuccess = useCallback((field) => (response) => {
    const url = response && response.url ? response.url : "";
    setValues((prev) => ({ ...prev, [field]: url }));
    setUploadedUrls((prev) => ({ ...prev, [field]: url }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const fieldList = useMemo(() => (schema && Array.isArray(schema.fields) ? schema.fields : []), [schema]);

  const labels = schema && schema.labels ? schema.labels : {};
  const hints = schema && schema.hints ? schema.hints : {};
  const widgets = schema && schema.widgets ? schema.widgets : {};

  const isTextarea = (field) => field === "description" || field === "about";

  const handleChange = (field, val) => {
    setValues((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const base = getApiBase();
    if (!base) {
      setError("Не задан REACT_APP_FW_API для запросов к API.");
      return;
    }

    setSaving(true);
    setSuccess("");
    setError("");
    try {
      const token = localStorage.getItem("token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${base}/v1/cpai/location-add-form-save`, {
        method: "POST",
        headers,
        body: JSON.stringify(values),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const normalizedErrors = normalizeFieldErrors(data);
        if (Object.keys(normalizedErrors).length > 0) {
          setErrors(normalizedErrors);
        }
        setError(data && data.message ? data.message : `Ошибка сохранения (код ${res.status}).`);
        return;
      }

      setSuccess("Локация успешно добавлена. Переходим к списку...");
      setTimeout(() => {
        navigate(`${process.env.PUBLIC_URL}/location-list`);
      }, 600);
    } catch (err) {
      setError("Ошибка сети при сохранении формы.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle="Добавить локацию"
        title="Добавить локацию"
        parent={
          <Link to={listPath} className="text-decoration-none">
            Список локаций
          </Link>
        }
      />
      <Container fluid={true} className="product-wrapper">
        {loading && <P className="text-muted mb-0">Загрузка формы...</P>}
        {!loading && error && <P className="text-danger mb-2">{error}</P>}
        {!loading && success && <P className="text-success mb-2">{success}</P>}

        {!loading && schema && (
          <Form onSubmit={onSubmit}>
            <Row>
              {fieldList.map((field) => {
                const widget = widgets[field] || { type: "input", inputType: "text" };
                const label = labels[field] || field;
                const hint = hints[field] || "";
                const fieldErrors = errors[field];
                const firstError = getFirstError(fieldErrors);
                const hasError = Boolean(firstError);

                if (widget.type === "fileUpload7Cloud") {
                  const cfg = {
                    ...DEFAULT_UPLOAD_CONFIG,
                    ...widget,
                    data: {
                      ...DEFAULT_UPLOAD_CONFIG.data,
                      ...(widget.data || {}),
                    },
                  };
                  return (
                    <Col md="12" key={field} className="mb-3">
                      <Label className="form-label d-block">{label}</Label>
                      <FileUpload7Cloud
                        maxSize={cfg.maxSize}
                        allowedExtensions={cfg.allowedExtensions}
                        accept={cfg.accept}
                        controller={cfg.controller}
                        data={cfg.data}
                        cors={cfg.cors}
                        withCredentials={cfg.withCredentials}
                        onSuccess={handleUploadSuccess(field)}
                      />
                      {uploadedUrls[field] ? (
                        <P className="text-muted small mt-1 mb-0 user-select-all">
                          URL: {uploadedUrls[field]}
                        </P>
                      ) : null}
                      {hint ? (
                        <P className="small mb-0 mt-1" style={{ color: "#6c757d" }}>
                          {hint}
                        </P>
                      ) : null}
                      {hasError ? (
                        <div className="invalid-feedback d-block">{firstError}</div>
                      ) : null}
                    </Col>
                  );
                }

                if (widget.type === "switch") {
                  return (
                    <Col md="12" key={field} className="mb-3">
                      <FormGroup check className="switch-showcase">
                        <Label className="switch">
                          <Input
                            type="checkbox"
                            checked={Boolean(values[field])}
                            onChange={(ev) => handleChange(field, ev.target.checked ? 1 : 0)}
                          />
                          <span className="switch-state"></span>
                        </Label>
                        <span className="ms-2">{label}</span>
                      </FormGroup>
                      {hint ? (
                        <P className="small mb-0 mt-1" style={{ color: "#6c757d" }}>
                          {hint}
                        </P>
                      ) : null}
                      {hasError ? (
                        <div className="invalid-feedback d-block">{firstError}</div>
                      ) : null}
                    </Col>
                  );
                }

                return (
                  <Col md="12" key={field} className="mb-3">
                    <Label className="form-label">{label}</Label>
                    <Input
                      className={hasError ? "form-control is-invalid" : "form-control"}
                      type={isTextarea(field) ? "textarea" : "text"}
                      rows={isTextarea(field) ? 4 : undefined}
                      value={values[field] ?? ""}
                      onChange={(ev) => handleChange(field, ev.target.value)}
                    />
                    {hasError ? <div className="invalid-feedback d-block">{firstError}</div> : null}
                    {hint ? (
                      <P className="small mb-0 mt-1" style={{ color: "#6c757d" }}>
                        {hint}
                      </P>
                    ) : null}
                  </Col>
                );
              })}
            </Row>

            <div style={{ marginBottom: 30 }}>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Сохранение..." : "Сохранить"}
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </Fragment>
  );
};

export default LocationAdd;
